/**
 * Card for a single simulation session in history lists.
 *
 * @module SimulationResultCard
 */

import type { CSSProperties, ReactNode } from 'react';
import { AlertTriangle, Calendar, Loader2, RotateCcw, TrendingDown, TrendingUp } from 'lucide-react';
import {
  examTypeLabels,
  type SimulationResult,
} from '@/types/simulation-results';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { SIMULATION_MODULE_COLORS } from '@/lib/simulationResultsChartData';
import { SIMULATION_MODULE_ICONS, SIMULATION_MODULE_SHORT_LABELS } from '@/lib/simulationModuleDisplay';
import {
  getFirstRetryableAnalysisModule,
  getModuleAnalysisDisplayInfo,
  getSimulationAnalysisDisplayInfo,
  isModuleReviewAvailable,
  type AnalysisDisplayInfo,
} from '@/lib/analysisStatus';

function AnalysisStateBadge({
  result,
}: {
  readonly result: SimulationResult;
}) {
  const { t } = useTranslation();
  const analysisInfo = getSimulationAnalysisDisplayInfo(result);

  if (analysisInfo.state === 'graded') {
    return (
      <Badge variant={result.passed ? 'default' : 'destructive'} className="text-xs">
        {result.passed
          ? t('common.passed', { defaultValue: 'Passed' })
          : t('common.failed', { defaultValue: 'Failed' })}
      </Badge>
    );
  }

  if (analysisInfo.state === 'failed') {
    return (
      <Badge
        variant="secondary"
        className="border-destructive/30 bg-destructive/10 text-xs text-destructive hover:bg-destructive/10"
      >
        <AlertTriangle className="mr-1 h-3 w-3" />
        {t('simulation.results.status.failed', { defaultValue: 'Analysis failed' })}
      </Badge>
    );
  }

  return (
    <Badge
      variant="secondary"
      className="border-amber-200 bg-amber-50 text-xs text-amber-700 hover:bg-amber-100"
    >
      {analysisInfo.state === 'analyzing' ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
      {analysisInfo.state === 'delayed'
        ? t('simulation.results.status.delayed', { defaultValue: 'Taking longer than expected' })
        : t('simulation.results.status.analysing', { defaultValue: 'Analysing...' })}
    </Badge>
  );
}

function UngradedModuleStatus({
  info,
}: {
  readonly info: AnalysisDisplayInfo;
}) {
  const { t } = useTranslation();

  if (info.state === 'failed') {
    return (
      <span className="flex shrink-0 items-center text-destructive">
        <AlertTriangle className="mr-1 h-3 w-3" />
        <span className="truncate">
          {info.canRetry
            ? t('simulation.results.status.retry', { defaultValue: 'Retry' })
            : t('simulation.results.status.failedShort', { defaultValue: 'Failed' })}
        </span>
      </span>
    );
  }

  if (info.state === 'delayed') {
    return (
      <span className="flex shrink-0 items-center text-amber-700">
        <Loader2 className="mr-1 h-2.5 w-2.5 animate-spin" />
        <span className="truncate">
          {t('simulation.results.status.delayedShort', { defaultValue: 'Delayed' })}
        </span>
      </span>
    );
  }

  return (
    <span className="flex shrink-0 items-center text-amber-600 animate-pulse">
      <Loader2 className="mr-1 h-2 w-2 animate-spin" />
      <span className="hidden sm:inline">
        {t('simulation.results.status.analysing', { defaultValue: 'Analysing...' })}
      </span>
    </span>
  );
}

function ResultModuleBreakdown({
  result,
  onModuleClick,
  onModuleRetry,
  moduleActionLabel,
  density = 'default',
}: {
  readonly result: SimulationResult;
  readonly onModuleClick?: (module: string) => void;
  readonly onModuleRetry?: (module: string) => void | Promise<void>;
  readonly moduleActionLabel?: string;
  readonly density?: 'default' | 'compact';
}) {
  const { t } = useTranslation();

  return (
    <div
      className={cn(
        'grid grid-cols-2 border-t border-border sm:grid-cols-2 lg:grid-cols-4',
        density === 'default'
          ? 'mt-6 grid-cols-2 gap-3 pt-5 sm:gap-4 lg:grid-cols-4'
          : 'mt-2 grid-cols-2 gap-2 pt-2.5 sm:grid-cols-2 lg:grid-cols-4',
      )}
    >
      {result.modules.map((module) => {
        const Icon = SIMULATION_MODULE_ICONS[module.module];
        const isGraded = module.is_graded;
        const analysisInfo = getModuleAnalysisDisplayInfo(module);
        const canRetry = analysisInfo.state === 'failed' && analysisInfo.canRetry && Boolean(onModuleRetry);
        const canOpenReview = isModuleReviewAvailable(module);
        const isInteractive = canOpenReview || canRetry;

        let progressColor = 'hsl(var(--muted))';
        if (isGraded) {
          progressColor = module.passed
            ? SIMULATION_MODULE_COLORS[module.module]
            : 'hsl(var(--destructive))';
        }

        return (
          <button
            key={module.module}
            type="button"
            disabled={!isInteractive}
            className={cn(
              'group flex w-full flex-col rounded-lg border border-transparent text-left transition-all focus:outline-none focus:ring-2 focus:ring-primary/50',
              density === 'default' ? 'gap-2 p-2.5' : 'gap-1 p-2',
              isInteractive
                ? 'cursor-pointer hover:border-border hover:bg-accent/50'
                : 'cursor-not-allowed opacity-70 grayscale-[0.5]',
              canRetry && 'hover:border-destructive/30 hover:bg-destructive/5',
            )}
            onClick={() => {
              if (canOpenReview) {
                onModuleClick?.(module.module);
                return;
              }
              if (canRetry) {
                void onModuleRetry?.(module.module);
              }
            }}
            aria-label={
              canOpenReview && moduleActionLabel
                ? `${moduleActionLabel} ${SIMULATION_MODULE_SHORT_LABELS[module.module]}`
                : undefined
            }
          >
            <div className="flex min-w-0 items-center gap-2.5">
              <div
                className={cn(
                  'shrink-0 rounded-md border border-border bg-background p-1.5 transition-colors',
                  isGraded &&
                    'group-hover:border-primary group-hover:bg-primary group-hover:text-primary-foreground',
                )}
              >
                <Icon className="h-3.5 w-3.5" style={{ color: 'inherit' }} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="mb-1 flex items-center justify-between text-[11px]">
                  <span className="truncate font-medium text-muted-foreground">
                    {t(`moduleSelection.modules.${module.module}.label`, {
                      defaultValue: SIMULATION_MODULE_SHORT_LABELS[module.module],
                    })}
                  </span>
                  {isGraded ? (
                    <span
                      className={cn(
                        'font-bold tracking-tight',
                        module.passed ? 'text-primary' : 'text-destructive',
                      )}
                    >
                      {module.percentage}%
                    </span>
                  ) : (
                    <UngradedModuleStatus info={analysisInfo} />
                  )}
                </div>
                {isGraded && analysisInfo.state === 'analyzing' ? (
                  <span className="flex shrink-0 items-center text-[10px] text-amber-600">
                    <Loader2 className="mr-1 h-2.5 w-2.5 animate-spin" aria-hidden />
                    {t('simulation.results.status.analysing', { defaultValue: 'Analysing...' })}
                  </span>
                ) : null}
                <Progress
                  value={isGraded ? module.percentage : 0}
                  className={cn('h-1.5', !isGraded && 'opacity-30')}
                  style={
                    {
                      '--progress-background': progressColor,
                    } as CSSProperties
                  }
                />
                {canOpenReview && moduleActionLabel ? (
                  <span className="font-ui mt-1 inline-flex text-[10px] font-semibold uppercase tracking-wide text-primary">
                    {moduleActionLabel}
                  </span>
                ) : null}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function SimulationResultCardCompact({
  result,
  onModuleClick,
  onModuleRetry,
  headerContent,
  moduleActionLabel,
}: {
  readonly result: SimulationResult;
  readonly onModuleClick?: (module: string) => void;
  readonly onModuleRetry?: (module: string) => void | Promise<void>;
  readonly headerContent?: ReactNode;
  readonly moduleActionLabel?: string;
}) {
  const { t } = useTranslation();
  const analysisInfo = getSimulationAnalysisDisplayInfo(result);
  const firstRetryableModule = getFirstRetryableAnalysisModule(result);
  return (
    <div className="rounded-xl border border-border bg-card p-3 shadow-sm transition-colors hover:bg-accent/10 sm:p-4">
      {headerContent ? (
        <div className="mb-3 border-b border-border pb-3">
          {headerContent}
        </div>
      ) : null}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-2.5 sm:items-center">
          <div
            className={`shrink-0 rounded-lg p-1.5 ${result.passed ? 'bg-[hsl(145,60%,45%)]/10' : 'bg-destructive/10'}`}
          >
            {result.passed ? (
              <TrendingUp className="h-4 w-4 text-[hsl(145,60%,45%)]" />
            ) : (
              <TrendingDown className="h-4 w-4 text-destructive" />
            )}
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-1.5">
              <Badge variant="outline" className="text-[11px] px-1.5 py-0">
                {examTypeLabels[result.examType]}
              </Badge>
              <Badge variant="secondary" className="text-[11px] px-1.5 py-0">
                {result.level}
              </Badge>
              <AnalysisStateBadge result={result} />
            </div>
            <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <Calendar className="h-3 w-3 shrink-0" />
              {new Date(result.date).toLocaleDateString(undefined, {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between gap-3 border-t border-border pt-2 sm:border-t-0 sm:pt-0">
          <div className="text-xl font-bold tracking-tight">
            {analysisInfo.state === 'graded' ? (
              `${result.percentage}%`
            ) : analysisInfo.state === 'failed' ? (
              <span className="text-destructive">--%</span>
            ) : (
              <span className="text-amber-600">--%</span>
            )}
          </div>
          {firstRetryableModule && onModuleRetry ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 shrink-0 font-ui text-xs text-destructive hover:text-destructive"
              onClick={() => void onModuleRetry(firstRetryableModule.module)}
            >
              <RotateCcw className="mr-1 h-3 w-3" />
              {t('simulation.results.status.retry', { defaultValue: 'Retry' })}
            </Button>
          ) : null}
        </div>
      </div>

      {result.modules.length > 0 && onModuleClick ? (
        <ResultModuleBreakdown
          result={result}
          onModuleClick={onModuleClick}
          onModuleRetry={onModuleRetry}
          moduleActionLabel={moduleActionLabel}
          density="compact"
        />
      ) : null}
    </div>
  );
}

function SimulationResultCardFull({
  result,
  onModuleClick,
  onModuleRetry,
  highlighted = false,
  headerContent,
  footerAction,
  moduleActionLabel,
}: {
  readonly result: SimulationResult;
  readonly onModuleClick?: (module: string) => void;
  readonly onModuleRetry?: (module: string) => void | Promise<void>;
  readonly highlighted?: boolean;
  readonly headerContent?: ReactNode;
  readonly footerAction?: ReactNode;
  readonly moduleActionLabel?: string;
}) {
  const { t } = useTranslation();
  const analysisInfo = getSimulationAnalysisDisplayInfo(result);
  return (
    <div
      className={cn(
        'rounded-xl border border-border bg-card p-4 shadow-sm transition-colors hover:bg-accent/10 sm:p-5',
        highlighted && 'ring-2 ring-primary/50 ring-offset-2 animate-[pulse_1.5s_ease-in-out_3]',
      )}
    >
      {headerContent ? (
        <div className="mb-5 border-b border-border pb-4">
          {headerContent}
        </div>
      ) : null}

      <div className="flex flex-col justify-between gap-5 sm:flex-row sm:items-center">
        <div className="flex items-start gap-4">
          <div
            className={`shrink-0 rounded-xl p-3 ${result.passed ? 'bg-[hsl(145,60%,45%)]/10' : 'bg-destructive/10'}`}
          >
            {result.passed ? (
              <TrendingUp className="h-6 w-6 text-[hsl(145,60%,45%)]" />
            ) : (
              <TrendingDown className="h-6 w-6 text-destructive" />
            )}
          </div>
          <div className="min-w-0">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="text-xs">
                {examTypeLabels[result.examType]}
              </Badge>
              <Badge variant="secondary" className="text-xs">
                {result.level}
              </Badge>
              <AnalysisStateBadge result={result} />
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5 whitespace-nowrap">
                <Calendar className="h-3.5 w-3.5" />
                {new Date(result.date).toLocaleDateString(undefined, {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between gap-6 border-t pt-4 sm:border-t-0 sm:justify-end sm:pt-0">
          <div className="text-left sm:text-right">
            <div className="text-2xl font-bold tracking-tight sm:text-3xl">
              {analysisInfo.state === 'graded' ? (
                `${result.percentage}%`
              ) : analysisInfo.state === 'failed' ? (
                <span className="text-destructive">--%</span>
              ) : (
                <span className="text-amber-600">--%</span>
              )}
            </div>
            <div className="text-xs font-medium text-muted-foreground sm:text-sm">
              {analysisInfo.state === 'graded' ? (
                `${result.totalScore}/${result.maxTotalScore}`
              ) : analysisInfo.state === 'failed' ? (
                t('simulation.results.status.failed', { defaultValue: 'Analysis failed' })
              ) : analysisInfo.state === 'delayed' ? (
                t('simulation.results.status.delayed', { defaultValue: 'Taking longer than expected' })
              ) : (
                t('simulation.results.status.analysing', { defaultValue: 'Analysing...' })
              )}
            </div>
          </div>
        </div>
      </div>

      <ResultModuleBreakdown
        result={result}
        onModuleClick={onModuleClick}
        onModuleRetry={onModuleRetry}
        moduleActionLabel={moduleActionLabel}
      />

      {footerAction ? (
        <div className="mt-5 flex justify-end border-t border-border pt-4">
          {footerAction}
        </div>
      ) : null}
    </div>
  );
}

export interface SimulationResultCardProps {
  readonly result: SimulationResult;
  /** Required for full variant (per-module review); omit for compact. */
  readonly onModuleClick?: (module: string) => void;
  readonly variant?: 'full' | 'compact';
  readonly highlighted?: boolean;
  readonly onModuleRetry?: (module: string) => void | Promise<void>;
  readonly headerContent?: ReactNode;
  readonly footerAction?: ReactNode;
  readonly moduleActionLabel?: string;
}

export function SimulationResultCard({
  result,
  onModuleClick,
  variant = 'full',
  highlighted = false,
  onModuleRetry,
  headerContent,
  footerAction,
  moduleActionLabel,
}: SimulationResultCardProps) {
  if (variant === 'compact') {
    return (
      <SimulationResultCardCompact
        result={result}
        onModuleClick={onModuleClick}
        onModuleRetry={onModuleRetry}
        headerContent={headerContent}
        moduleActionLabel={moduleActionLabel}
      />
    );
  }
  return (
    <SimulationResultCardFull
      result={result}
      onModuleClick={onModuleClick}
      onModuleRetry={onModuleRetry}
      highlighted={highlighted}
      headerContent={headerContent}
      footerAction={footerAction}
      moduleActionLabel={moduleActionLabel}
    />
  );
}
